/**
 * Argus — Helpers pentru live screen streams (MSE).
 *
 * Header binary frame WS (24 bytes prefix, big-endian):
 *  [0..15]  stream_id (BINARY 16 = UUID v7)
 *  [16]     chunk_type (0=init segment, 1=media segment, 2=keyframe)
 *  [17]     flags
 *  [18..21] sequence number (uint32 BE)
 *  [22..23] reserved
 *  [24..]   payload fMP4
 */

import { apiFetch } from './api';
import { wsClient } from './wsClient';

export interface VideoConfigPreset {
  readonly label: string;
  readonly width: number;
  readonly height: number;
  readonly fps: number;
  readonly bitrate: number;
}

export const VIDEO_CONFIG_PRESETS: Record<string, VideoConfigPreset> = {
  low: { label: 'Low (5fps · 500kbps · 720p)', width: 1280, height: 720, fps: 5, bitrate: 500_000 },
  medium: { label: 'Medium (10fps · 1Mbps · 720p)', width: 1280, height: 720, fps: 10, bitrate: 1_000_000 },
  high: { label: 'High (15fps · 2Mbps · 1080p)', width: 1920, height: 1080, fps: 15, bitrate: 2_000_000 },
};

export interface StreamStartResponse {
  commandId: string;
  streamId: string;
  status: string;
  expiresAt: string;
}

export interface ActiveStream {
  streamId: string;
  agentId: string;
  agentHostname: string;
  startedBy: string;
  startedByName: string;
  justification: string;
  sources: string[];
  videoConfig: { width: number; height: number; fps: number; bitrate: number } | null;
  startedAt: string | null;
  createdAt: string;
}

export type StreamQuality = 'eco' | 'standard' | 'hd' | 'hd_plus';

export const QUALITY_BITRATES: Record<StreamQuality, number> = {
  eco: 500_000,
  standard: 1_500_000,
  hd: 3_000_000,
  hd_plus: 6_000_000,
};

export const QUALITY_LABELS: Record<StreamQuality, string> = {
  eco: 'Eco (500 Kbps)',
  standard: 'Standard (1.5 Mbps)',
  hd: 'HD (3 Mbps)',
  hd_plus: 'HD+ (6 Mbps)',
};

export interface StreamStartOptions {
  sources?: Array<'screen' | 'camera' | 'audio'>;
  cameraId?: string;
  audioId?: string;
  quality?: StreamQuality;
}

/**
 * Bloc ws-direct-stream-control 2026-05-10: emit `stream_start` via WS direct.
 * ws-node generează streamId server-side + INSERT stream_sessions; răspunsul include
 * streamId pentru subscribe la binary frames. `commandId` NU e exposed în noul flow
 * (Promise rezolvă direct cu data) — caller-ii care îl așteaptau primesc empty string.
 */
export async function emitStreamStart(
  agentIdHex: string,
  justification: string,
  videoConfig: VideoConfigPreset,
  options: StreamStartOptions = {}
): Promise<StreamStartResponse> {
  const sources = options.sources ?? ['screen'];
  const hasAudio = sources.includes('audio');
  const hasVideo = sources.includes('camera') || sources.includes('screen');
  const params: Record<string, unknown> = { sources, justification };
  if (hasVideo) {
    const videoConfigPayload: Record<string, number | string> = {
      width: videoConfig.width,
      height: videoConfig.height,
      fps: videoConfig.fps,
      bitrate: videoConfig.bitrate,
    };
    if (options.cameraId !== undefined) {
      videoConfigPayload['camera_id'] = options.cameraId;
    }
    if (options.quality) {
      videoConfigPayload['quality'] = options.quality;
    }
    params['video_config'] = videoConfigPayload;
  }
  if (hasAudio) {
    params['audio_config'] = {
      sample_rate: 48000,
      channels: 1,
      bits: 16,
      audio_id: options.audioId,
    };
  }
  const data = await wsClient.requestStreamStart(agentIdHex, params);
  return {
    commandId: '',
    streamId: data.streamId,
    status: data.status,
    expiresAt: data.expiresAt ?? '',
  };
}

export async function emitStreamStop(
  agentIdHex: string,
  streamIdHex: string,
  justification: string = 'Manual stream stop'
): Promise<{ commandId: string }> {
  await wsClient.requestStreamStop(agentIdHex, streamIdHex, justification);
  return { commandId: '' };
}

export async function fetchActiveStreams(): Promise<ActiveStream[]> {
  const { data } = await apiFetch<ActiveStream[]>('/api/v1/streams/active');
  return data;
}

export function getOperatorWsUrl(): string {
  const explicit = import.meta.env.VITE_WS_BASE_URL;
  if (explicit) {
    return `${explicit}/ws/operator`;
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws/operator`;
}
